import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FalkeTekLogo } from "../components/Brand";
import { api, formatApiErrorDetail } from "../lib/api";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }
    if (password !== password2) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      const u = await loginWithToken(data);
      setDone(true);
      setTimeout(() => {
        if (u.role === "admin" || u.role === "buchhalter") navigate("/admin");
        else if (u.role === "vertriebler") navigate("/vertriebler");
        else navigate("/app");
      }, 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-falke-warmwhite flex flex-col">
      <header className="px-6 py-5 bg-white border-b border-falke-blue/10">
        <FalkeTekLogo />
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md falke-card p-6 sm:p-8" data-testid="reset-password-card">
          <h2 className="text-2xl font-heading font-semibold text-falke-blue mb-2">Neues Passwort vergeben</h2>
          <p className="text-sm text-falke-anthracite/70 mb-6">
            Wähle ein sicheres Passwort mit mindestens 6 Zeichen.
          </p>

          {!done && (
            <form onSubmit={onSubmit} data-testid="reset-password-form">
              <label className="block text-sm font-medium text-falke-blue mb-1.5">Neues Passwort</label>
              <div className="relative mb-4">
                <input
                  data-testid="reset-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-falke-blue/15 focus:outline-none focus:ring-2 focus:ring-falke-orange/40 focus:border-falke-orange"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-falke-blue/60 hover:text-falke-orange transition p-1"
                  aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  data-testid="reset-password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <label className="block text-sm font-medium text-falke-blue mb-1.5">Passwort wiederholen</label>
              <input
                data-testid="reset-password-confirm"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-falke-blue/15 focus:outline-none focus:ring-2 focus:ring-falke-orange/40 focus:border-falke-orange mb-2"
              />

              {error && <div className="text-sm text-red-600 my-3" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full mt-4" data-testid="reset-submit">
                {loading ? "Wird gespeichert..." : "Passwort speichern"}
              </button>
            </form>
          )}

          {done && (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="mx-auto text-falke-orange mb-3" size={42} />
              <h3 className="font-heading text-lg text-falke-blue mb-2">Passwort geändert</h3>
              <p className="text-sm text-falke-anthracite/70">Du wirst gleich weitergeleitet...</p>
            </div>
          )}

          <div className="mt-6 text-center text-sm text-falke-anthracite/70">
            <Link to="/login" className="text-falke-orange font-semibold hover:underline" data-testid="link-back-login">
              Zurück zum Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
